import { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  Field,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchableDropdown } from '../../SearchableDropdown';
import { FilterOperator } from './filter.types';
import type { IActiveFilter, IFilterableColumn } from './filter.types';
import { OPERATOR_CONFIG, OPERATORS_BY_TYPE } from './filter.const';
import { getDefaultValue } from './filter.utils';
import { FilterValueInput } from './FilterValueInput';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '8px',
    minWidth: '280px',
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
});

export type FilterFormView = 'select-column' | 'configure-filter';

interface FilterFormProps {
  columns: IFilterableColumn[];
  onSubmit: (filter: IActiveFilter) => void;
  onCancel: () => void;
  title?: string;
  submitText?: string;
  initialView?: FilterFormView;
  initialFilter?: IActiveFilter;
  disableFilterSelect?: boolean;
}

export const FilterForm = ({
  columns,
  onSubmit,
  onCancel,
  title = 'Add filter',
  submitText = 'Apply',
  initialView = 'select-column',
  initialFilter,
  disableFilterSelect = false,
}: FilterFormProps) => {
  const styles = useStyles();

  const [view, setView] = useState<FilterFormView>(initialView);
  const [selectedColumn, setSelectedColumn] =
    useState<IFilterableColumn | null>(initialFilter?.column ?? null);
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator>(
    initialFilter?.operator ?? FilterOperator.Eq
  );
  const [value, setValue] = useState<string | string[]>(
    initialFilter?.value ?? ''
  );

  // Reset form when initialFilter changes (for editing different filters)
  useEffect(() => {
    if (initialFilter) {
      setSelectedColumn(initialFilter.column);
      setSelectedOperator(initialFilter.operator);
      setValue(initialFilter.value);
      setView('configure-filter');
    } else {
      setSelectedColumn(null);
      setSelectedOperator(FilterOperator.Eq);
      setValue('');
      setView(initialView);
    }
  }, [initialFilter, initialView]);

  const getDefaultOperator = (column: IFilterableColumn): FilterOperator => {
    const operators = column.operators ?? OPERATORS_BY_TYPE[column.type];
    return operators[0] ?? FilterOperator.Eq;
  };

  const handleColumnSelect = (columnKey: string) => {
    const column = columns.find((c) => c.key === columnKey);
    if (column) {
      if (column.toggleOptions) {
        setSelectedColumn(column);
        setSelectedOperator(FilterOperator.Eq);
        setValue(column.toggleOptions[1].value);
        setView('configure-filter');
      } else {
        const defaultOperator = getDefaultOperator(column);
        setSelectedColumn(column);
        setSelectedOperator(defaultOperator);
        setValue(getDefaultValue(defaultOperator));
        setView('configure-filter');
      }
    }
  };

  const handleSubmit = () => {
    if (!selectedColumn) return;

    onSubmit({
      column: selectedColumn,
      operator: selectedOperator,
      value,
    });
  };

  const isToggle = Boolean(selectedColumn?.toggleOptions);
  const operatorConfig = OPERATOR_CONFIG[selectedOperator];
  const showValueInput = operatorConfig?.requiresValue ?? true;
  const availableOperators = selectedColumn
    ? selectedColumn.operators ?? OPERATORS_BY_TYPE[selectedColumn.type]
    : [];

  // View: Select column
  if (view === 'select-column') {
    return (
      <div className={styles.container}>
        <span className={styles.title}>{title}</span>
        <ColumnPicker columns={columns} onSelect={handleColumnSelect} />
      </div>
    );
  }

  // View: Configure filter
  return (
    <div className={styles.container}>
      <span className={styles.title}>{title}</span>

      <Field label="Filter">
        <Dropdown
          value={selectedColumn?.label ?? ''}
          selectedOptions={selectedColumn ? [selectedColumn.key] : []}
          disabled={disableFilterSelect}
          onOptionSelect={(_, data) => {
            const column = columns.find((c) => c.key === data.optionValue);
            if (column) {
              const defaultOperator = getDefaultOperator(column);
              setSelectedColumn(column);
              setSelectedOperator(defaultOperator);
              setValue(getDefaultValue(defaultOperator));
            }
          }}
        >
          {columns.map((column) => (
            <Option key={column.key} value={column.key}>
              {column.label}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {!isToggle && (
        <Field label="Operator">
          <Dropdown
            value={OPERATOR_CONFIG[selectedOperator].label}
            selectedOptions={[selectedOperator]}
            onOptionSelect={(_, data) => {
              const newOperator = data.optionValue as FilterOperator;
              setSelectedOperator(newOperator);
              setValue(getDefaultValue(newOperator));
            }}
          >
            {availableOperators.map((op) => (
              <Option key={op} value={op}>
                {OPERATOR_CONFIG[op].label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      )}

      {selectedColumn && (isToggle || showValueInput) && (
        <FilterValueInput
          column={selectedColumn}
          operator={selectedOperator}
          value={value}
          onChange={setValue}
        />
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={
            !selectedColumn ||
            (!isToggle &&
              showValueInput &&
              (Array.isArray(value) ? value.length === 0 : !value.trim()))
          }
        >
          {submitText}
        </Button>
        <Button appearance="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

interface ColumnPickerProps {
  columns: IFilterableColumn[];
  onSelect: (columnKey: string) => void;
}

const ColumnPicker = ({ columns, onSelect }: ColumnPickerProps) => (
  <SearchableDropdown
    placeholder="Select filter"
    options={columns.map((c) => ({ key: c.key, label: c.label }))}
    onSelect={onSelect}
  />
);
